import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Quotes from './pages/Quotes';
import QuoteDetail from './pages/QuoteDetail';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import Staff from './pages/Staff';
import StaffDetail from './pages/StaffDetail';
import MyProfile from './pages/MyProfile';
import PropertyDetail from './pages/PropertyDetail';
import ItemsServices from './pages/ItemsServices';
import Labour from './pages/Labour';
import Referrals from './pages/Referrals';
import Reports from './pages/Reports';
import EmailSettings from './pages/EmailSettings';
import QuoteAcceptancePage from './pages/QuoteAcceptancePage';
import ExportXeroInvoice from './pages/ExportXeroInvoice';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // Cache results for 30s so navigating between pages doesn't refire
      // the same query (e.g. notifications, dropdowns). Mutations still
      // invalidate explicitly when data changes.
      staleTime: 30_000,
    },
  },
});

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
}

function RoleRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/quote/acceptance/:token" element={<QuoteAcceptancePage />} />
            <Route path="/" element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="clients" element={<RoleRoute roles={[0, 5, 6]}><Clients /></RoleRoute>} />
              <Route path="clients/:id" element={<RoleRoute roles={[0, 5, 6]}><ClientDetail /></RoleRoute>} />
              <Route path="quotes" element={<RoleRoute roles={[0, 5, 6]}><Quotes /></RoleRoute>} />
              <Route path="quotes/:id" element={<RoleRoute roles={[0, 5, 6]}><QuoteDetail /></RoleRoute>} />
              <Route path="jobs" element={<RoleRoute roles={[0, 5, 6]}><Jobs /></RoleRoute>} />
              <Route path="jobs/:id" element={<RoleRoute roles={[0, 5, 6]}><JobDetail /></RoleRoute>} />
              <Route path="invoices" element={<RoleRoute roles={[0, 5]}><Invoices /></RoleRoute>} />
              <Route path="invoices/:id" element={<RoleRoute roles={[0, 5]}><InvoiceDetail /></RoleRoute>} />
              <Route path="staff" element={<RoleRoute roles={[0, 5]}><Staff /></RoleRoute>} />
              <Route path="staff/:id" element={<RoleRoute roles={[0, 5]}><StaffDetail /></RoleRoute>} />
              <Route path="profile" element={<MyProfile />} />
              <Route path="properties/:id" element={<RoleRoute roles={[0, 5, 6]}><PropertyDetail /></RoleRoute>} />
              <Route path="items-services" element={<RoleRoute roles={[0]}><ItemsServices /></RoleRoute>} />
              <Route path="labour" element={<RoleRoute roles={[0]}><Labour /></RoleRoute>} />
              <Route path="referrals" element={<RoleRoute roles={[0, 5]}><Referrals /></RoleRoute>} />
              <Route path="reports" element={<RoleRoute roles={[0, 5]}><Reports /></RoleRoute>} />
              <Route path="email-settings" element={<RoleRoute roles={[0, 5]}><EmailSettings /></RoleRoute>} />
              <Route path="invoices/export-xero" element={<RoleRoute roles={[0, 5]}><ExportXeroInvoice /></RoleRoute>} />
            </Route>
          </Routes>
        </Router>
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: { fontFamily: 'Inter, sans-serif' }
          }}
        />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
